import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectTransById, updateTrans } from 'services/trans';
import 'styles/laudo-base.css';
import useSWR, { mutate } from 'swr';

const defaultTemplate = [
  'Exame realizado sob sedação com Midazolam e anestesia tópica oro-faringea com Lidocaína',
  'spray a 10% introduzido transdutor transesofágico, sem intercorrências.',
  'Átrio direito de dimensões normais, sem sinais de ecos anômalos.',
  '',
  'Ventrículo direito de dimensões normais, sem sinais de ecos anômalos.',
  '',
  'Átrio esquerdo de dimensões normais, sem sinais de ecos anômalos, apresenta',
  'desembocadura das veias pulmonares sem sinais de obstrução e apêndice atrial',
  'sem evidências de trombos.',
  '',
  'Ventrículo esquerdo com paredes de espessura normal, contratilidade segmentar preservada,',
  'parâmetro de função sistólica dentro dos limites da normalidade.',
  '',
  'Valvas átrio-ventriculares e sigmóides morfologicamente normais, sem sinais de estenose',
  'ou refluxo significativo ao estudo com Doppler.',
  '',
  'O estudo com Doppler e com MFC não evidenciou shunts intercavitários.',
  '',
  'Pericárdio normal.',
  '',
  'Aorta ascendente, arco aórtico e aorta torácica descedente de diâmetros normais,',
  'sem evidências de placas ateromatosas.',
  '',
  'ECOCARDIOGRAMA TRANSESOFÁGICO DENTRO DOS LIMITES DA NORMALIDADE.',
].join('\n');

export default function FormTrans() {
  const history = useHistory();
  const { id } = useParams();
  const {
    data: trans,
    isLoading,
    error,
  } = useSWR(id ? ['trans', id] : null, () => selectTransById(id));
  const [transDescricao, setTransDescricao] = useState('');
  const [editarTransChecked, setEditarTransChecked] = useState(false);
  const [savingButton, setSavingButton] = useState(null); // 'DEFINITIVO' | 'PARCIAL' | null
  const [confirmaGravacaoModal, setConfirmaGravacaoModal] = useState(false);
  const isDefinitivo =
    (trans && trans.STATUS_GRAVACAO === 'DEFINITIVO') || savingButton === 'DEFINITIVO';
  const transCode = String(trans?.CODTRANS ?? trans?.CODIGO ?? id ?? '--');
  const rowCount = useMemo(() => {
    const lineCount = (transDescricao.match(/\n/g) || []).length + 1;
    return Math.max(20, lineCount + 4);
  }, [transDescricao]);

  useEffect(() => {
    if (!trans) return;
    const value = String(trans.ECOTRANS ?? '');
    setTransDescricao(value);
    setEditarTransChecked(value.trim() === defaultTemplate.trim());
  }, [trans]);

  const handleGravar = async (tipoGravacao = 'PARCIAL') => {
    if (!id) return toast.warn('Erro de validação!');

    try {
      setSavingButton(tipoGravacao);
      const res = await updateTrans(id, {
        ECOTRANS: transDescricao,
        tipoGravacao,
      });
      await mutate(['trans', id]);
      toast.success(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao gravar.');
    } finally {
      setSavingButton(null);
      setConfirmaGravacaoModal(false);
    }
  };

  const handleLoadTemplate = () => {
    if (!transDescricao.trim()) {
      setTransDescricao(defaultTemplate);
      setEditarTransChecked(true);
      return;
    }

    setTransDescricao(defaultTemplate);
    setEditarTransChecked(true);
  };

  const handleToggleEditarTrans = (checked) => {
    if (!checked) {
      setEditarTransChecked(false);
      return;
    }

    handleLoadTemplate();
  };

  const handleAnexar = () => {
    toast.info('em desenvolvimento...');
  };

  const handleVisualizar = () => {
    if (!id) {
      toast.warn('Erro de validação!');
      return;
    }

    history.push(`/visualizacao-trans/${id}`);
  };

  return (
    <>
      <Card className="mb-2 mt-2 no-print legacy-eco-card trans-legacy-card">
        <Card.Header className="legacy-header trans-legacy-header">
          <div className="legacy-header-side">
            <div className="legacy-header-eco-wrap">
              <span className="legacy-mini-label">Cod Trans:</span>
              <span className="legacy-mini-value">{transCode}</span>
            </div>
          </div>

          <div className="legacy-header-title-wrap">
            <div className="legacy-header-title">Eco Transesofágico</div>
          </div>

          <div className="legacy-header-side legacy-header-side-right">
            <div className="legacy-header-date-wrap">
              <span className="legacy-mini-label">Data Trans:</span>
              <span className="legacy-mini-value trans-date-value">{trans?.dataTrans || '--'}</span>
            </div>
          </div>
        </Card.Header>

        {!isLoading && !error && trans && (
          <Card.Body className="pt-3">
            <div className="trans-simple-header mb-2">
              <div className="legacy-section-title-wrap mb-0 trans-simple-title">
                <h2 className="legacy-section-title mb-1">Eco Transesofágico</h2>
                <Form.Check
                  id="editar-trans-checkbox"
                  className="trans-edit-check"
                  type="checkbox"
                  label="Editar Trans"
                  checked={editarTransChecked}
                  disabled={isDefinitivo}
                  onChange={(event) => handleToggleEditarTrans(event.target.checked)}
                />
              </div>
            </div>

            <Form.Group className="mb-0">
              <Form.Control
                as="textarea"
                className="trans-editor"
                rows={rowCount}
                value={transDescricao}
                onChange={(event) => setTransDescricao(event.target.value)}
                readOnly={isDefinitivo}
              />
            </Form.Group>
          </Card.Body>
        )}

        {isLoading && (
          <Card.Body>
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          </Card.Body>
        )}

        {error && !isLoading && (
          <Card.Body className="text-center text-danger py-5">
            {error.message || 'Erro ao carregar o laudo.'}
          </Card.Body>
        )}

        {!isLoading && !error && trans && (
          <Card.Footer className="text-center no-print">
            <Button
              variant="outline-success"
              onClick={() => setConfirmaGravacaoModal(true)}
              disabled={!!savingButton || isDefinitivo}
            >
              {savingButton === 'DEFINITIVO' ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Gravar definitivo'
              )}
            </Button>{' '}
            <Button
              variant="outline-primary"
              onClick={() => handleGravar('PARCIAL')}
              disabled={!!savingButton || isDefinitivo}
            >
              {savingButton === 'PARCIAL' ? (
                <Spinner animation="border" size="sm" />
              ) : (
                'Gravar parcial'
              )}
            </Button>{' '}
            <Button variant="secondary" onClick={handleVisualizar}>
              Visualizar
            </Button>{' '}
            <Button variant="info" onClick={handleAnexar}>
              Anexar
            </Button>
          </Card.Footer>
        )}
      </Card>

      <Modal show={confirmaGravacaoModal} centered onHide={() => setConfirmaGravacaoModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmação de gravação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Tem certeza que deseja gravar o laudo definitivamente?</p>
          Você não poderá mais alterá-lo.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => setConfirmaGravacaoModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => handleGravar('DEFINITIVO')}
            disabled={!!savingButton || isDefinitivo}
          >
            Gravar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
